import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useAuth } from './use-auth';
import type {
  ModelSummary,
  ModelSchema,
  ModelDisplayConfig,
  ModelVersionSummary,
  SignalVariant,
} from '@arcanum-hq/shared-types';

interface ModelsListResponse {
  data: ModelSummary[];
}
interface ModelDetailResponse {
  data: ModelSchema;
}
interface ModelVersionsResponse {
  data: ModelVersionSummary[];
}

export function useModels() {
  const { user } = useAuth();
  return useQuery({
    queryKey: queryKeys.models.list(),
    queryFn: () => api.get<ModelsListResponse>('/api/models'),
    // Don't fire on unauthenticated /builder visits — avoids benign 401s +
    // console-error noise. (builder-unauth-query-gate, 2026-05-16)
    enabled: !!user,
  });
}

// builder-ux-overhaul Phase 5 F02 fix (2026-05-22)
// GET /api/admin/models (requireAdmin) returns ALL latest model families
// including inactive ones — same ModelSummary shape as GET /api/models
// (incl. isActive). Mirrored exactly from useModels() but hits the admin
// endpoint so disabled rows appear in the admin <ModelList> table and the
// "Enable" button becomes reachable.
// NOTE: useModels() is intentionally kept on GET /api/models (active-only);
// <NewConversationModal> must NOT show disabled models.
export function useAdminModels() {
  const { user } = useAuth();
  return useQuery({
    queryKey: queryKeys.models.adminList(),
    queryFn: () => api.get<ModelsListResponse>('/api/admin/models'),
    enabled: !!user,
  });
}

export function useModel(id: string | null) {
  return useQuery({
    queryKey: queryKeys.models.detail(id || ''),
    queryFn: () => api.get<ModelDetailResponse>(`/api/models/${id}`),
    enabled: !!id,
  });
}

export function useModelVersions(modelId: string | null) {
  return useQuery({
    queryKey: queryKeys.models.versions(modelId || ''),
    queryFn: () =>
      api.get<ModelVersionsResponse>(`/api/models/${modelId}/versions`),
    enabled: !!modelId,
  });
}

interface IngestModelArgs {
  formData: FormData;
  force?: boolean;
}

export function useIngestModel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ formData, force }: IngestModelArgs) => {
      const path = force
        ? '/api/models/ingest/stream?force=true'
        : '/api/models/ingest/stream';
      const data = await api.uploadStream<{ modelId: string }>(path, formData);
      return { data };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.models.all });
    },
  });
}

export function useUpdateModelConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      config,
    }: {
      id: string;
      config: Partial<ModelDisplayConfig>;
    }) => api.put(`/api/models/${id}/config`, config),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.models.detail(vars.id),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.models.all });
    },
  });
}

// agent-ux-optimization Phase 6 Session 2 (2026-05-08)
// PATCH /api/admin/models/:id/signal-variant — admin assigns the Signal tab
// variant for a model. Invalidates BOTH detail + list query keys so the
// admin `<ModelList>` row, the Builder Signal tab, and the
// `<NewConversationModal>` model dropdown all reflect the change without
// a page reload (I4 cache-coherence fix from Phase 6 gap-analysis).
export function useUpdateSignalVariant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      signal_variant,
    }: {
      id: string;
      signal_variant: SignalVariant | null;
    }) =>
      api.patch(`/api/admin/models/${id}/signal-variant`, { signal_variant }),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.models.detail(vars.id),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.models.list() });
    },
  });
}

// builder-ux-overhaul Phase 5 Session 3 (2026-05-22)
// PATCH /api/admin/models/:id/enabled — admin enables or soft-retires a model
// family. Invalidates detail + list() (new-deal dropdown) + adminList()
// (admin <ModelList> — F02 fix: disabled row stays visible with updated
// isActive after the PATCH so the Enable button re-appears immediately).
// list() covers <NewConversationModal> (active-only — disabled family drops
// out of new-deal creation automatically — § 4.10 / § 11 R8).
// adminList() covers the admin <ModelList> table (all families incl inactive).
export function useUpdateModelEnabled() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch(`/api/admin/models/${id}/enabled`, { isActive }),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.models.detail(vars.id),
      });
      // drives the new-deal dropdown (active-only) — § 11 R8
      queryClient.invalidateQueries({ queryKey: queryKeys.models.list() });
      // drives the admin table (all families incl inactive) — F02 fix
      queryClient.invalidateQueries({ queryKey: queryKeys.models.adminList() });
    },
  });
}

// 2026-05-27
// PATCH /api/admin/models/:id/name — admin renames a model's display string.
// Invalidates detail + list() (new-deal dropdown) + adminList() (admin
// <ModelList>) so the rename propagates to the modal label, admin row,
// and any cached schema without a page reload. Mirrors the cache-flush
// shape from useUpdateModelEnabled exactly.
export function useUpdateModelName() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.patch(`/api/admin/models/${id}/name`, { name }),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.models.detail(vars.id),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.models.list() });
      queryClient.invalidateQueries({ queryKey: queryKeys.models.adminList() });
    },
  });
}

export function useDeleteModel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/models/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.models.all });
    },
  });
}
